<template>
  <Listbox as="div" :class="[classes]" class="!border-none p-0 relative bg-transparent">
    <ListboxLabel
      v-if="label"
      class="block text-sm font-medium text-gray-700 dark:text-dark-body-text dark:bg-dark-box-background"
      :for="name">
      {{ label }}
    </ListboxLabel>
    <ListboxButton as="template">
      <div class="flex mt-1">
        <div class="relative flex items-stretch flex-grow focus-within:z-10">
          <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>

          <span v-if="is_searching" class="absolute inset-y-0 flex items-center pr-2 pointer-events-none right-6">
            <RotateCw class="w-5 h-5 text-gray-500 animate-spin" aria-hidden="true" />
          </span>

          <span v-if="showIcon" class="absolute inset-y-0 flex items-center pr-2 pointer-events-none left-2">
            <Building2 class="ml-1 w-3.5 h-3.5 text-gray-500" />
          </span>

          <input
            ref="searchInput"
            :value="search_term"
            :disabled="disabled"
            autocomplete="off"
            :placeholder="`${
              selected_items.find((item) => item.id == 'this_record')
                ? 'This Record'
                : selected_items?.length
                  ? selected_items?.length == 1
                    ? selected_items[0]?.name
                    : `${translatedPlaceholder} (${selected_items?.length})`
                  : `${translatedPlaceholder}`
            }`"
            :class="[
              search_term?.length > 0 ? 'rounded-br-none rounded-tr-none' : '',
              disabled ? 'text-common-grey' : '',
              inputClasses,
              isBoxBackground ? 'bg-light-box-background dark:bg-dark-box-background' : 'bg-transparent',
              showIcon ? 'pl-10' : '',
              'appearance-none block w-full px-3  py-2  border-gray-300 rounded-lg  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light '
            ]"
            class="block w-full transition ease-in-out rounded-lg focus:ring-blue focus:border-blue-light placeholder-common-grey border-light-divider-border dark:border-dark-divider-border duration-side-menu-transition-duration text-light-body-text dark:text-dark-body-text"
            @blur="show_results = false"
            @click="search"
            @keydown="
              ($event) => {
                if ($event.keyCode == 32) {
                  $event.stopPropagation()
                }
              }
            "
            @input="
              ($event) => {
                show_results = true
                search_term = $event.target.value
              }
            ">
        </div>
      </div>
    </ListboxButton>

    <transition
      v-show="show_results"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <ListboxOptions
        static
        :class="[
          isBoxBackground ? 'bg-light-box-background dark:bg-dark-box-background' : '',
          clicked && show_results ? 'ring-1 ring-black ring-opacity-5' : ''
        ]"
        class="absolute inset-0 top-[42px] z-10 w-full bg-white max-h-60 rounded-md text-base overflow-auto focus:outline-none sm:text-sm"
        style="min-height: fit-content">
        <ListboxOption v-if="selected_items?.length">
          <div class="flex justify-between">
            <div :class="''" class="relative py-2 pl-2 pr-4 transition ease-in-out rounded-lg group duration-side-menu-transition-duration">
              <span
                :class="['text-light-body-text dark:text-dark-body-text']"
                class="block truncate transition ease-in-out duration-side-menu-transition-duration">
                {{ selected_items?.length }} {{ $t("items selected") }}
              </span>
            </div>
            <div class="relative cursor-pointer text-common-grey top-2 right-2" @click="reset">
              {{ $t("Clear") }}
            </div>
          </div>
        </ListboxOption>

        <ListboxOption v-if="thisRecordSelection" v-slot="{}" @click="handleSelectThisRecord()">
          <div
            :class="''"
            class="relative py-2 pl-8 pr-4 transition ease-in-out rounded-lg cursor-pointer group/name hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
            <span
              :class="[
                'text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text',
                { 'font-bold': selected_items.find((item) => item.id == 'this_record') }
              ]"
              class="block truncate transition ease-in-out duration-side-menu-transition-duration">
              {{ $t("This Record") }}
            </span>

            <span
              v-if="selected_items.find((item) => item.id == 'this_record')"
              :class="[
                'text-light-brand-blue dark:text-dark-brand-blue',
                'absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
              ]">
              <Check class="w-5 h-5" aria-hidden="true" />
            </span>
            <span v-else>
              <Check
                :class="['text-common-grey', 'absolute inset-y-0 left-0 flex items-center pl-1.5']"
                class="h-full w-7 opacity-60 group-hover/name:text-white absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration"
                aria-hidden="true" />
            </span>
          </div>
        </ListboxOption>

        <ListboxOption
          v-for="item in selected_items.filter((item) => item.id !== 'this_record')"
          v-slot="{}"
          :key="item"
          :value="item"
          @click="handleSelect(item)">
          <div
            :class="''"
            class="relative py-2 pl-8 pr-4 transition ease-in-out rounded-lg cursor-pointer group/name hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
            <span
              :class="[
                `${
                  isSelected(item) ? 'font-extrabold' : 'font-normal'
                } text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text`
              ]"
              class="block truncate transition ease-in-out duration-side-menu-transition-duration">
              {{ Record.from(item).name }}
            </span>

            <span
              v-if="isSelected(item)"
              :class="[
                'text-light-brand-blue dark:text-dark-brand-blue',
                'absolute group-hover/name:text-white inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
              ]">
              <Check class="w-5 h-5" aria-hidden="true" />
            </span>
            <span v-else>
              <Check
                :class="['text-common-grey', 'absolute inset-y-0 left-0 flex items-center pl-1.5']"
                class="h-full w-7 opacity-60 absolute group-hover/name:text-white inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration"
                aria-hidden="true" />
            </span>
          </div>
        </ListboxOption>
        <ListboxOption v-for="item in search_results"
                       v-slot="{}"
                       :key="item"
                       :value="item"
                       @click="handleSelect(item)">
          <div
            v-if="!selected_items?.find((selected_item) => selected_item.id == item.id)"
            :class="''"
            class="relative py-2 pl-8 pr-4 transition ease-in-out rounded-lg cursor-pointer group/name hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
            <span
              :class="['text-light-body-text dark:text-dark-body-text group-hover/name:text-dark-body-text']"
              class="block truncate transition ease-in-out duration-side-menu-transition-duration">
              {{ Record.from(item).name }}
            </span>

            <span
              v-if="isSelected(item)"
              :class="[
                'text-light-brand-blue dark:text-dark-brand-blue',
                'absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration'
              ]">
              <Check class="w-5 h-5" aria-hidden="true" />
            </span>
            <span v-else>
              <Check
                :class="['text-common-grey', 'absolute inset-y-0 left-0 flex items-center pl-1.5']"
                class="h-full w-7 opacity-60 group-hover/name:text-white absolute inset-y-0 left-0 flex items-center pl-1.5 transition ease-in-out duration-side-menu-transition-duration"
                aria-hidden="true" />
            </span>
          </div>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue"
import dataService from "@/services/data.service"
import { ChevronDown, Check, RotateCw, Building2 } from "lucide-vue-next"
import { Record } from "@/models/record"

export default {
  components: {
    RotateCw,
    Listbox,
    ListboxLabel,
    ListboxOptions,
    ListboxOption,
    ListboxButton,
    Check,
    ChevronDown,
    Building2
  },
  props: {
    thisRecordSelection: null,
    name: {
      type: String,
      required: true
    },

    label: {
      type: String,
      required: false
    },
    initialValue: {
      type: Array,
      default: () => []
    },

    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },
    inputClasses: {
      type: String,
      default: "text"
    },
    classes: {
      type: String,
      default: "text"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    endpoint: {
      type: String,
      required: true
    },
    focusTrigger: {
      type: Number,
      required: false
    },
    objectType: {
      type: String,
      default: "company"
    },
    multiSelect: {
      type: Boolean,
      default: true
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => []
    },
    debounce: {
      type: Number,
      default: 0
    }
  },
  emits: ["update:currentItems"],
  data() {
    return {
      Record,
      is_searching: false,
      show_results: true,
      search_term: "",
      search_results: [],
      selected_items: [],
      clicked: false,
      change_timeout: null
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    }
  },
  watch: {
    value() {
      this.selected_items = this.value
    },
    focusTrigger() {
      setTimeout(() => {
        this.$refs.searchInput?.focus()
        this.$refs.searchInput?.click()
      }, 200)
    },
    search_term() {
      if (this.search_term != null && this.show_results) {
        clearTimeout(this.change_timeout)
        this.change_timeout = setTimeout(() => {
          this.search()
        }, this.debounce)
      }
    }
  },
  created() {
    this.selected_items = this.initialValue?.length ? this.initialValue : []
    this.show_results = false
  },

  methods: {
    handleSelectThisRecord() {
      this.selected_items = []
      this.selected_items = [{ id: "this_record" }]
      this.$emit("update:currentItems", this.selected_items)
    },
    isSelected(item) {
      return this.selected_items?.findIndex((i) => i.id == item.id) != -1 ? true : false
    },

    handleSelect(item) {
      this.selected_items = this.selected_items.filter((item) => item.id !== "this_record")
      if (this.isSelected(item)) {
        let index = this.selected_items?.findIndex((i) => i.id == item.id)
        this.selected_items?.splice(index, 1)
      } else {
        if (this.multiSelect) {
          this.selected_items?.push(item)
        } else {
          this.selected_items = [item]
        }
      }
      this.show_results = false
      this.search_term = ""

      this.$emit("update:currentItems", this.selected_items)
    },

    search() {
      this.clicked = true
      this.is_searching = true

      // filter[this.searchField] = this.search_term
      const data = { object_types: [this.objectType] }
      if (this.objectType == "contact") {
        data["filter"] = [
          this.$store.state.integration.metrics_mapping.contact_first_name_metric,
          "in",
          `'${this.search_term?.split(" ")[0]}'`,
          this.search_term?.split(" ").length > 1 && this.search_term?.split(" ")[1] ? "and" : "or",
          this.$store.state.integration.metrics_mapping.contact_last_name_metric,
          "in",
          `'${
            this.search_term?.split(" ").length > 1 && this.search_term?.split(" ")[1] ? this.search_term?.split(" ")[1] : this.search_term
          }'`,
          "or",
          this.$store.state.integration.metrics_mapping.contact_email_metric,
          "in",
          `'${this.search_term?.split(" ")[0]}'`
        ].join(" ")
      } else {
        data["filter"] = [
          this.$store.state.integration.metrics_mapping[`${this.objectType}_name_metric`],
          "in",
          `'${this.search_term}'`
        ].join(" ")
      }
      dataService
        .post(this.endpoint, data)
        .then((result) => {
          this.show_results = true
          this.is_searching = false
          this.search_results = result.data.results
          this.$refs.searchInput.focus()
        })
        .catch(() => {
          this.is_searching = false
        })
    },
    reset() {
      this.selected_items = []
      this.show_results = false
      this.search_term = ""

      this.$emit("update:currentItems", this.selected_items)
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
